/**
 * Validates if a URL is safe for redirection to prevent Open Redirect vulnerabilities
 * @param {string} url - The URL to validate
 * @param {string[]} allowedDomains - Optional array of allowed domains (defaults to current origin only)
 * @returns {boolean} - True if the URL is safe for redirection
 */
export const isSafeRedirectUrl = (url, allowedDomains = []) => {
    if (!url || typeof url !== 'string') {
        return false
    }

    try {
        // Allow relative URLs that start with / but not //
        if (url.startsWith('/') && !url.startsWith('//')) {
            return true
        }

        // Parse the URL
        const parsedUrl = new URL(url, window.location.origin)

        // Check if the URL has the same origin as the current page
        if (parsedUrl.origin === window.location.origin) {
            return true
        }

        // Check against allowed domains if provided
        if (allowedDomains.length > 0) {
            const urlHost = parsedUrl.hostname.toLowerCase()
            return allowedDomains.some(domain => {
                const normalizedDomain = domain.toLowerCase()
                return urlHost === normalizedDomain || urlHost.endsWith(`.${normalizedDomain}`)
            })
        }

        // If no allowed domains specified, only allow same-origin URLs
        return false
    } catch (error) {
        // Invalid URL format
        return false
    }
}

/**
 * Sanitizes a URL for safe redirection
 * @param {string} url - The URL to sanitize
 * @param {string} fallbackUrl - Fallback URL if the provided URL is unsafe (defaults to '/')
 * @param {string[]} allowedDomains - Optional array of allowed domains
 * @returns {string} - The sanitized URL or fallback URL
 */
export const sanitizeRedirectUrl = (url, fallbackUrl = '/', allowedDomains = []) => {
    return isSafeRedirectUrl(url, allowedDomains) ? url : fallbackUrl
}
